/* Produktformular: Variantenwechsel, Wechsel zwischen verknüpften Listings ohne Seitenwechsel, In den Warenkorb per Fetch. */

const root = () => window.Shopify?.routes?.root || '/';

async function softNavigate(url, push = true) {
  document.documentElement.classList.add('is-soft-loading');
  try {
    const response = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error(`Soft navigation failed: ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const next = doc.querySelector('#MainContent');
    const current = document.querySelector('#MainContent');
    if (!next || !current) throw new Error('Main content missing');
    const scrollY = window.scrollY;
    current.replaceWith(next);
    document.title = doc.title;

    /*
      Die Kaufleiste steht außerhalb des Hauptbereichs und wurde beim weichen Seitenwechsel
      deshalb nie erneuert. Sichtbar wurde das bei den Größen: Die liegen in eigenen
      Listings, ein Größenwechsel ist also ein Seitenwechsel — und die Leiste zeigte
      weiter die Größe des vorigen Listings, bis zufällig ein Variantenwechsel kam.
    */
    const leisteNeu = doc.querySelector('kg-sticky-buy');
    const leisteAlt = document.querySelector('kg-sticky-buy');
    if (leisteNeu && leisteAlt) leisteAlt.replaceWith(leisteNeu);
    next.querySelectorAll('script[src]').forEach((script) => {
      const src = script.getAttribute('src');
      if (document.querySelector(`script[src="${src}"]`)) return;
      const clone = document.createElement('script');
      [...script.attributes].forEach((attribute) => clone.setAttribute(attribute.name, attribute.value));
      document.body.append(clone);
    });
    if (push) window.history.pushState({ soft: true }, '', url);
    window.scrollTo({ top: scrollY, behavior: 'instant' });
    document.dispatchEvent(new CustomEvent('page:replaced', { detail: { url } }));
  } catch {
    window.location.href = url;
  } finally {
    document.documentElement.classList.remove('is-soft-loading');
  }
}

window.addEventListener('popstate', (event) => {
  if (event.state?.soft || document.documentElement.dataset.softNavigated) softNavigate(window.location.href, false);
});

class KgProductForm extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.url = this.dataset.url;
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-option-index]')) this.onOptionChange(event.target);
    });
    this.addEventListener('click', (event) => {
      const linked = event.target.closest('a[data-linked-url]');
      if (!linked) return;
      event.preventDefault();
      this.goToLinked(linked.dataset);
    });
    this.addEventListener('submit', (event) => {
      if (event.target.matches('[data-product-form]')) this.onSubmit(event);
    });
  }

  /*
    Die gewählten Optionswerte als IDs. Shopify sucht damit selbst die Variante — eine
    Teilmenge genügt, Fehlendes fällt auf den ersten verfügbaren Wert zurück.

    Früher lag die ganze Variantenliste als JSON in der Seite und das Suchen passierte hier.
    Liquid gibt davon aber nur die ersten 250 heraus, ohne Fehler: Ab da hätten Sorten
    gefehlt, ohne dass es jemandem auffällt.
  */
  get selectedOptionValues() {
    const ids = [];
    this.querySelectorAll('[data-option-index]').forEach((element) => {
      if (element.tagName === 'SELECT') {
        const option = element.selectedOptions[0];
        if (option?.dataset.optionValueId) ids.push(option.dataset.optionValueId);
      } else if (element.checked && element.dataset.optionValueId) {
        ids.push(element.dataset.optionValueId);
      }
    });
    return ids;
  }

  onOptionChange(element) {
    if (element.tagName === 'SELECT') {
      const option = element.selectedOptions[0];
      if (option?.dataset.linkedUrl) {
        this.goToLinked({ ...option.dataset, value: option.value });
        return;
      }
    }
    this.querySelectorAll('.option-tile').forEach((tile) => {
      const input = tile.querySelector('input');
      if (input) tile.classList.toggle('is-selected', input.checked);
    });
    this.applySelection();
  }

  /* Holt den neu gerenderten Abschnitt und setzt die Teile ein, die sich geändert haben. */
  async applySelection() {
    const werte = this.selectedOptionValues;
    if (!werte.length) return;

    this.setAttribute('loading', '');
    try {
      const response = await fetch(`${this.url}?option_values=${werte.join(',')}&section_id=${this.sectionId}`);
      if (!response.ok) throw new Error(`Section ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      /*
        Keine Variante zur Kombination: Shopify liefert dann `null`. Die Auswahl wird trotzdem
        übernommen, damit der Kunde sieht, was er gewählt hat.
      */
      const roh = doc.querySelector('[data-variant-json]')?.textContent || 'null';
      const variant = JSON.parse(roh);
      this.toggleAttribute('unavailable', !variant);

      /*
        Solange die Schrittfolge offen ist, bleibt der Auswahlbereich stehen: Der Schuber lebt
        darin, und ein Austausch wuerde ihn mitten in der Auswahl wegreissen. Die Preise und
        Knoepfe laufen weiter mit; die Listen holt die Schrittfolge beim Schliessen einmal nach.
      */
      const bereiche = ['[data-price]', '[data-buy-buttons]', '[data-delivery]', '[data-quick-add-image]'];
      if (!this.hasAttribute('data-keep-options')) bereiche.unshift('[data-options]');
      bereiche.forEach((selector) => {
        const source = doc.querySelector(selector);
        const target = this.querySelector(selector);
        if (source && target) target.replaceChildren(...source.childNodes);
      });
      const eigenes = this.querySelector('[data-variant-json]');
      if (eigenes) eigenes.textContent = roh;
      const idFeld = this.querySelector('input[name="id"]');
      if (idFeld && variant) idFeld.value = variant.id;

      /*
        Adresse und Galerie nur anfassen, wenn das Formular zum Produkt der Seite gehört und
        nicht losgelöst läuft (Auswahlmodus im Schnellkauf-Drawer).
      */
      /*
        Das Ereignis zuerst: Daran hängen Kaufleiste, Nährwerte und Mengenaktion. Käme es
        nach der Galerie, würde ein Fehler dort sie alle mit stillegen.
      */
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));

      if (variant && window.location.pathname === this.url && !this.hasAttribute('data-detached')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url);
        try {
          const gallery = document.querySelector('kg-product-gallery');
          if (gallery?.showVariant) gallery.showVariant(variant);
          else if (variant.featured_media?.id) gallery?.goTo(variant.featured_media.id);
        } catch {
          /* Galerie bleibt beim vorherigen Bild */
        }
      }
    } catch {
      /*
        Schlägt der Abruf fehl, darf die Seite nicht beim alten Stand stehenbleiben: Im
        Formular stünde dann die vorige Variante, und in den Warenkorb ginge etwas anderes,
        als der Kunde gewählt hat. Auf der Produktseite wird deshalb neu geladen, im Drawer
        der Kauf gesperrt.
      */
      if (window.location.pathname === this.url && !this.hasAttribute('data-detached')) {
        window.location.assign(`${this.url}?option_values=${werte.join(',')}`);
      } else {
        this.setAttribute('unavailable', '');
      }
    } finally {
      this.removeAttribute('loading');
    }
  }

  async goToLinked(data) {
    const { productHandle, optionName, value, linkedUrl } = data;
    let target = linkedUrl;
    try {
      const response = await fetch(`${root()}products/${productHandle}.js`);
      const product = await response.json();
      const names = product.options.map((option) => (typeof option === 'string' ? option : option.name));
      const wanted = {};
      this.querySelectorAll('[data-option-index]').forEach((element) => {
        const fieldset = element.closest('.product-option');
        const name = fieldset?.querySelector('.product-option__label')?.textContent.trim();
        if (!name) return;
        if (element.tagName === 'SELECT' || element.checked) wanted[name] = element.value;
      });
      wanted[optionName] = value;
      const score = (variant) => names.reduce((sum, name, index) => sum + (variant.options[index] === wanted[name] ? 1 : 0), 0);
      const candidates = product.variants
        .filter((variant) => variant.options[names.indexOf(optionName)] === value)
        .sort((a, b) => score(b) - score(a) || Number(b.available) - Number(a.available));
      if (candidates[0]) target = `${root()}products/${productHandle}?variant=${candidates[0].id}`;
    } catch {
      /* Fallback auf die vorberechnete Adresse */
    }
    /* Im Schnellkauf faengt kg-quick-add das Ereignis ab und laedt das Ziel-Listing in den Drawer. */
    const navigate = new CustomEvent('product:navigate', { bubbles: true, cancelable: true, detail: { url: target } });
    this.dispatchEvent(navigate);
    if (navigate.defaultPrevented) return;
    document.documentElement.dataset.softNavigated = 'true';
    softNavigate(target);
  }

  async onSubmit(event) {
    event.preventDefault();
    const form = event.target;
    const button = form.querySelector('[data-add-to-cart]');
    const message = form.querySelector('[data-form-message]');
    if (!button || button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || '');
      const cart = await (await fetch(`${root()}cart.js`)).json();
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart, item: data, source: 'product-form' } }));
      this.dispatchEvent(new CustomEvent('product:added', { bubbles: true, detail: { cart, item: data } }));
      if (!document.querySelector('kg-cart[data-open-on-add]')) this.showMessage(message, form.dataset.addedText, `${root()}cart`, form.dataset.viewCartText);
    } catch (error) {
      this.showMessage(message, error.message || form.dataset.errorText);
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  showMessage(element, text, linkUrl, linkText) {
    if (!element) return;
    element.textContent = text || '';
    if (linkUrl && linkText) {
      const link = document.createElement('a');
      link.href = linkUrl;
      link.textContent = linkText;
      element.append(' ', link);
    }
    element.hidden = false;
    clearTimeout(this.messageTimer);
    this.messageTimer = setTimeout(() => {
      element.hidden = true;
    }, 5000);
  }
}

customElements.define('kg-product-form', KgProductForm);

export { softNavigate };
